<script setup lang="ts">
//#region imports
import { ref, computed, onBeforeMount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, minLength, maxLength, requiredIf } from '@vuelidate/validators';
import { useApi } from '@/api';
import { usePermissions } from '@/composables/usePermissions';
import { useValidate } from '@/composables/useValidate';
import { useError } from '@/composables/useError';
import { useTypedStore } from '@/store';

import { usePopupManagerCache, type CacheProps } from '@/composables/usePopupManagerCache';
import type { EmitsToListeners } from '@/types/core';
import type {
  GetDomainsManagementPlatformServiceDomainsResponse,
  PostDomainsManagementServiceRotationGroupsData,
} from '@/types/openapi-ts';
//#endregion

//#region types
interface DomainsData {
  adserverDomains: GetDomainsManagementPlatformServiceDomainsResponse['payload'];
  landingsStorageDomains: GetDomainsManagementPlatformServiceDomainsResponse['payload'];
  promoApiDomains: GetDomainsManagementPlatformServiceDomainsResponse['payload'];
  redirectDomains: GetDomainsManagementPlatformServiceDomainsResponse['payload'];
}

interface FormType {
  id?: string;
  name: string;
  description: string;
  auto_replace_banned_domains: boolean;
  auto_replace_domains: string;
  auto_replace_domains_countries_list: string[];
  domains: {
    promo_api_domain_id: string;
    adserver_domain_id: string;
    landings_storage_domain_id: string;
    redirect_domain_id: string;
  };
  type: string;
}

interface Props extends CacheProps {
  groupId?: string | null
}

interface  Emits {
  'success': [],
  'close': [],
}

export interface RotationGroupParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  groupId: null,
});
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const { fetchData } = usePopupManagerCache(props, async () => {
  const result = await api.getServiceOrigins({});
  const { data: { payload: serviceOrigins } } = result;
  const serviceOriginIdsMap = Object.values(serviceOrigins).reduce<Record<string, string>>((acc, serviceOrigin) => {
    acc[serviceOrigin.service_type] = serviceOrigin.id;

    return acc;
  }, {});
  return await Promise.all([
    getDomains(serviceOriginIdsMap.adserver!),
    getDomains(serviceOriginIdsMap.landings_storage!),
    getDomains(serviceOriginIdsMap.promo_api!),
    getDomains(serviceOriginIdsMap.redirect!),
  ]);
});
//#endregion

//#region const
const rules = {
  name: {
    required,
    minLength: minLength(1),
    maxLength: maxLength(255),
  },
  description: {
    required,
    minLength: minLength(1),
    maxLength: maxLength(1024),
  },
  auto_replace_domains_countries_list: {
    required: requiredIf(() => form.value.auto_replace_banned_domains && form.value.auto_replace_domains !== 'any_country'),
  },
  domains: {
    redirect_domain_id: { required },
    adserver_domain_id: { required },
    landings_storage_domain_id: { required },
    promo_api_domain_id: { required },
  },
};
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const { t } = useI18n();
const { hasPermission } = usePermissions();

const { checkField, isInvalidField, isInvalidForm } = useValidate({
  rules,
  form: computed(() => {
    return form.value || {};
  }),
});

const { getError } = useError({
  errors: computed(() => {
    return errors.value || [];
  }),
});
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const errors = ref({});

const form = ref<FormType>({
  name: '',
  description: '',
  auto_replace_banned_domains: false,
  auto_replace_domains: 'disabled',
  auto_replace_domains_countries_list: [],
  domains: {
    promo_api_domain_id: '',
    adserver_domain_id: '',
    landings_storage_domain_id: '',
    redirect_domain_id: '',
  },
  type: '',
});

const domains = ref<DomainsData>({
  adserverDomains: [],
  landingsStorageDomains: [],
  promoApiDomains: [],
  redirectDomains: [],
});
//#endregion

//#region computed
const countries = computed(() => store.getters['misc/countries']);

const isEdit = computed(() => {
  return props.groupId;
});

const pPopupTitle = computed(() => {
  return `${t(`domainManagement.popup.${isEdit.value ? 'edit' : 'add'}`)} ${t('domainManagement.popup.rotationGroup')}`;
});

const isDefaultRotationGroup = computed(() => {
  return form.value.type === 'default';
});

const canViewDomains = computed(() => {
  return hasPermission('service_rotation_groups_domains_view');
});

const canEditDomains = computed(() => {
  return hasPermission('service_rotation_groups_domains_edit');
});

const countriesList = computed({
  get() {
    if (form.value.auto_replace_domains === 'any_country' || !form.value.auto_replace_banned_domains) {
      return [];
    }

    return form.value.auto_replace_domains_countries_list;
  },
  set(val) {
    form.value.auto_replace_domains_countries_list = val;
  },
});
//#endregion

//#region methods
async function open() {
  if(isEdit.value) {
    const { data: { payload } } = await api.getRotationGroupById(props.groupId!);

    form.value.id = payload.id;
    form.value.name = payload.name;
    form.value.description = payload.description || '';
    form.value.type = payload.type;
    form.value.auto_replace_domains = payload.auto_replace_domains;
    form.value.domains = { ...payload.domains };
  }

  if (form.value.auto_replace_domains === 'disabled') {
    form.value.auto_replace_banned_domains = false;
    form.value.auto_replace_domains = 'any_country';
    form.value.auto_replace_domains_countries_list = [];
  }

  await nextTick();
  isVisible.value = true;
}

function handleClose() {
  emit('close');
  isVisible.value = false;
}

function handleSave() {
  isLoading.value = true;
  errors.value = {};

  const payload = {
    name: form.value.name,
    description: form.value.description,
    auto_replace_domains: 'disabled',
    ...(form.value.auto_replace_banned_domains && {
      auto_replace_domains: form.value.auto_replace_domains,
      ...(form.value.auto_replace_domains !== 'any_country' && {
        auto_replace_domains_countries_list: form.value.auto_replace_domains_countries_list,
      }),
    }),

    domains: {
      ...form.value.domains,
    },
  } as PostDomainsManagementServiceRotationGroupsData['body'];

  const promise = isEdit.value
    ? api.updateRotationGroup(form.value.id!, payload)
    : api.createRotationGroup(payload);

  promise.then(() => {
    emit('success');
    isVisible.value = false;
  }).catch((error) => {
    errors.value = error.data.errors;
  }).finally(() => {
    isLoading.value = false;
  });
}

async function getDomains(platform_service_origin_id: string) {
  const params = { offset: 0, limit: 1000, platform_service_origin_id };
  const { data: { payload } } = await api.getServiceDomains(params);
  return payload;
}
//#endregion

//#region hooks
onBeforeMount(async () => {
  const [adserverDomains, landingsStorageDomains, promoApiDomains, redirectDomains] = await fetchData();
  domains.value = {
    adserverDomains,
    landingsStorageDomains,
    promoApiDomains,
    redirectDomains,
  };
});
//#endregion

defineExpose({
  open,
  fetchData,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="rotation-group-popup"
    :title="pPopupTitle"
    :is-loading="isLoading"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiInput
          v-model="form.name"
          :label="$t(`domainManagement.popup.form.group_name.label`)"
          :placeholder="$t(`domainManagement.popup.form.group_name.placeholder`)"
          :disabled="isDefaultRotationGroup"
          size="big"
          autosize
          required
          :is-error="isInvalidField('name')"
          @blur="checkField('name')"
        />
        <UiTextarea
          v-model="form.description"
          :label="$t('domainManagement.popup.form.group_description.label')"
          required
          :placeholder="$t(`domainManagement.popup.form.group_description.placeholder`)"
          :rows="3"
          :is-error="isInvalidField('description')"
          @blur="checkField('description')"
        />
        <UiRadioGroup
          v-model="form.auto_replace_banned_domains"
          :label="$t('domainManagement.popup.form.auto_replace_banned_domains.label')"
          :disabled="isDefaultRotationGroup"
          :options="[
            {
              label: $t('domainManagement.popup.form.auto_replace_banned_domains.yes'),
              value: true,
            },
            {
              label: $t('domainManagement.popup.form.auto_replace_banned_domains.no'),
              value: false,
            }
          ]"
        />
        <UiRadioGroup
          v-model="form.auto_replace_domains"
          :label="$t('domainManagement.popup.form.countries_to_check_availability.label')"
          :disabled="!form.auto_replace_banned_domains"
          :options="[
            {
              label: $t('domainManagement.popup.form.countries_to_check_availability.any'),
              value: 'any_country',
            },
            {
              label: $t('domainManagement.popup.form.countries_to_check_availability.only_selected'),
              value: 'only_selected_countries',
            },
            {
              label: $t('domainManagement.popup.form.countries_to_check_availability.except_selected'),
              value: 'except_selected_countries',
            }
          ]"
        />
        <UiSelectElement
          v-model="countriesList"
          :label="$t('domainManagement.popup.form.countries_list')"
          :placeholder="$t('domainManagement.popup.form.countries_placeholder')"
          :list="countries"
          size="big"
          search-param="name"
          option-value-param="code"
          filterable
          multiple
          collapse-tag-tooltip
          :max-collapse-tags="1"
          :error="getError('auto_replace_domains_countries_list', 0) || getError('auto_replace_domains_countries_list.0', 0)"
          :disabled="!form.auto_replace_banned_domains || form.auto_replace_domains === 'any_country'"
        />
        <template v-if="canViewDomains">
          <UiSelectElement
            v-model="form.domains.redirect_domain_id"
            :label="$t('domainManagement.popup.form.redirect_domain_id.label')"
            :placeholder="$t('domainManagement.popup.empty')"
            :list="domains.redirectDomains"
            :disabled="!canEditDomains"
            size="big"
            filterable
            option-label-param="domain_name"
            option-name-param="domain_name"
          />
          <UiSelectElement
            v-model="form.domains.adserver_domain_id"
            :label="$t('domainManagement.popup.form.adserver_domain_id.label')"
            :placeholder="$t('domainManagement.popup.empty')"
            :list="domains.adserverDomains"
            :disabled="!canEditDomains"
            size="big"
            filterable
            option-label-param="domain_name"
            option-name-param="domain_name"
          />
          <UiSelectElement
            v-model="form.domains.landings_storage_domain_id"
            :label="$t('domainManagement.popup.form.landings_storage_domain_id.label')"
            :placeholder="$t('domainManagement.popup.empty')"
            :list="domains.landingsStorageDomains"
            :disabled="!canEditDomains"
            size="big"
            filterable
            option-label-param="domain_name"
            option-name-param="domain_name"
          />
          <UiSelectElement
            v-model="form.domains.promo_api_domain_id"
            :label="$t('domainManagement.popup.form.promo_api_domain_id.label')"
            :placeholder="$t('domainManagement.popup.empty')"
            :list="domains.promoApiDomains"
            :disabled="!canEditDomains"
            size="big"
            filterable
            option-label-param="domain_name"
            option-name-param="domain_name"
          />
        </template>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          :disabled="isLoading"
          @click="handleClose"
        >
          {{ $t(`domainManagement.popup.cancel`) }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          :disabled="isInvalidForm || isLoading"
          @click="handleSave"
        >
          {{ $t(`domainManagement.popup.save`) }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
